// src/components/Procurement/POTimelineLog.jsx
import React, { useEffect, useState, useCallback } from 'react';
import api from '@/lib/axios';
import { Loader2, Clock, RotateCcw } from 'lucide-react';
import { motion } from 'framer-motion';

function pickTs(log) {
  // Your table has changed_at (timestamptz); keep fallbacks just in case older rows exist
  return log.changed_at || log.timestamp || log.time || log.at || log.created_at || null;
}
function parseTs(ts) {
  const d = ts ? new Date(ts) : null;
  return Number.isFinite(d?.getTime?.()) ? d : null;
}
function actorLabel(log) {
  // Prefer new actor_* fields, then graceful fallbacks
  return (
    log.actor_name ||
    log.user_full_name ||
    log.user_name ||
    log.user_email ||
    (log.actor_id ? `User #${log.actor_id}` : null) ||
    (log.changed_by ? `User #${log.changed_by}` : null)
  );
}
function normalizeLogs(raw) {
  const arr = Array.isArray(raw) ? raw : Array.isArray(raw?.logs) ? raw.logs : [];
  return arr
    .map((l) => ({ ...l, __tsRaw: pickTs(l), __ts: parseTs(pickTs(l)) }))
    .sort((a, b) => (b.__ts?.getTime?.() ?? 0) - (a.__ts?.getTime?.() ?? 0));
}

const POTimelineLog = ({ poId }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchLogs = useCallback(async () => {
    if (!poId) return;
    setLoading(true);
    setError(null);
    try {
      const endpoints = [
        `/po/activity/logs/${poId}`, // preferred
        `/po/log/${poId}`,           // legacy fallback
      ];
      let data;
      let lastErr;
      for (const ep of endpoints) {
        try {
          const res = await api.get(ep);
          data = res.data;
          break;
        } catch (e) {
          lastErr = e;
          if (e?.response?.status !== 404) throw e; // bubble non-404s
        }
      }
      if (!data) throw lastErr || new Error('No log endpoint matched');
      setLogs(normalizeLogs(data));
    } catch (err) {
      console.error('Failed to fetch PO log:', err);
      const msg =
        err?.response?.status === 403
          ? 'You do not have permission to view PO logs.'
          : err?.response?.status === 401
          ? 'Your session expired. Please sign in again.'
          : 'Unable to load PO log.';
      setError(msg);
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [poId]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  return (
    <motion.div
      className="bg-gradient-to-br from-slate-900 to-gray-800 p-5 rounded-xl border border-gray-700 shadow-lg"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-white tracking-wide">📜 PO Activity Timeline</h2>
        <button
          onClick={fetchLogs}
          className="inline-flex items-center gap-2 text-sm px-3 py-1.5 rounded bg-white/10 hover:bg-white/15 border border-white/20"
          title="Refresh"
        >
          <RotateCcw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {loading ? (
        <div className="text-blue-300 flex items-center gap-2 text-sm">
          <Loader2 className="animate-spin" /> Loading timeline...
        </div>
      ) : error ? (
        <div className="text-amber-300 text-sm">{error}</div>
      ) : logs.length === 0 ? (
        <p className="text-gray-400 text-sm">No activity recorded for this PO.</p>
      ) : (
        <ul className="space-y-4">
          {logs.map((log, i) => {
            const ts = log.__ts || parseTs(pickTs(log));
            const by = actorLabel(log);

            return (
              <li
                key={log.log_id ?? `${i}-${pickTs(log) ?? 'na'}`}
                className="border-l-4 border-cyan-500 pl-4"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-sm text-white">
                    <strong>Status:</strong> {log.status || '—'}
                  </p>
                  {log.event_type ? (
                    <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded bg-cyan-600/20 border border-cyan-600/40 text-cyan-200">
                      {log.event_type}
                    </span>
                  ) : null}
                  {log.actor_role ? (
                    <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded bg-purple-600/20 border border-purple-600/40 text-purple-200">
                      {log.actor_role}
                    </span>
                  ) : null}
                </div>

                <p className="text-xs text-gray-400 flex items-center gap-1 mt-1">
                  <Clock className="w-3 h-3" />
                  {ts ? ts.toLocaleString() : '—'}
                  {by ? <span className="ml-2">• by {by}</span> : null}
                </p>

                {log.notes && (
                  <p className="text-xs text-yellow-300 mt-1 italic">“{log.notes}”</p>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </motion.div>
  );
};

export default POTimelineLog;
